import { useSyncExternalStore, useCallback } from 'react';
import * as Y from 'yjs';
import { Flex, Text, Icon, IconButton, Tooltip, Empty, icon, iconWeight } from '@nula/core-ui';
import { clsx } from 'clsx';
import { useImperative } from '@/features/canvas/engine/wiring/imperative';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import styles from './frames-panel.module.css';

export interface FrameSummary {
  id: string;
  title: string;
  iconName: string;
  childCount: number;
  position: { x: number; y: number };
  size: { width: number; height: number };
}

const FRAMES_FALLBACK: ReadonlyArray<FrameSummary> = [];

/**
 * Walk the doc's nodes Map once and produce a stable-by-content list of
 * all frame summaries. Used as the getSnapshot for useSyncExternalStore.
 *
 * Identity-stability: returns a NEW array each call, but caches the
 * shape on the doc; if every frame's tracked fields match the cached
 * tuple we return the cached array. Without this caching,
 * useSyncExternalStore would loop infinitely. Same pattern useFrameData
 * uses — see engine/hooks/useFrameData.ts for the precedent.
 */
const FRAMES_CACHE = new WeakMap<Y.Doc, FrameSummary[]>();

function readFramesSnapshot(doc: Y.Doc | null | undefined, binding: CanvasBinding = TOP_LEVEL_BINDING): ReadonlyArray<FrameSummary> {
  if (!doc) return FRAMES_FALLBACK;
  const nodes = canvasRoots(doc, binding).nodes;
  const next: FrameSummary[] = [];
  nodes.forEach((node) => {
    if (!(node instanceof Y.Map)) return;
    if (node.get('type') !== 'frame') return;
    const id = node.get('id');
    if (typeof id !== 'string') return;
    const data = node.get('data');
    const dataMap = data instanceof Y.Map ? data : undefined;
    const title = (dataMap?.get('title') as string | undefined) ?? '';
    const iconName = (dataMap?.get('icon') as string | undefined) ?? 'folder';
    const childOrder = dataMap?.get('childOrder');
    const childCount =
      childOrder instanceof Y.Array ? childOrder.length : 0;
    const pos = node.get('position') as { x: number; y: number } | undefined;
    const size = node.get('size') as { width: number; height: number } | null | undefined;
    next.push({
      id,
      title,
      iconName,
      childCount,
      position: pos ?? { x: 0, y: 0 },
      size: size ?? { width: 200, height: 200 },
    });
  });

  // Identity-stability: shallow-compare against last cached list. If
  // the values match we return the cached reference so
  // useSyncExternalStore doesn't loop.
  const prev = FRAMES_CACHE.get(doc);
  if (prev && prev.length === next.length) {
    let equal = true;
    for (let i = 0; i < prev.length; i++) {
      const a = prev[i];
      const b = next[i];
      if (
        a.id !== b.id ||
        a.title !== b.title ||
        a.iconName !== b.iconName ||
        a.childCount !== b.childCount ||
        a.position.x !== b.position.x ||
        a.position.y !== b.position.y ||
        a.size.width !== b.size.width ||
        a.size.height !== b.size.height
      ) {
        equal = false;
        break;
      }
    }
    if (equal) return prev;
  }
  FRAMES_CACHE.set(doc, next);
  return next;
}

function useFrames(doc: Y.Doc | null | undefined, binding: CanvasBinding): ReadonlyArray<FrameSummary> {
  return useSyncExternalStore(
    (onChange) => {
      if (!doc) return () => {};
      const nodes = canvasRoots(doc, binding).nodes;
      // observeDeep so we catch frame data changes (title/icon/childOrder
      // edits) AND top-level adds/removes.
      nodes.observeDeep(onChange);
      return () => nodes.unobserveDeep(onChange);
    },
    () => readFramesSnapshot(doc, binding),
  );
}

export interface FramesPanelProps {
  doc: Y.Doc | null;
  /** Optional: invoked when the user requests the panel to close
   *  (e.g. via a future close button). The Drawer that mounts this
   *  owns the open/close lifecycle today. */
  onClose?: () => void;
}

/**
 * Side-panel listing every frame on the canvas. Clicking a row pans
 * the canvas to center the frame and selects it via the imperative
 * bridge — the panel mounts in a Drawer portal that's a sibling of
 * the engine's RF tree, so it cannot call useReactFlow directly.
 * useImperative() reads the bridge's handle from context.
 *
 * Read-only v1 — no reordering or rename here (rename via the
 * frame's own toolbar).
 *
 * Reads frames live from Yjs so collaborator-driven adds/renames/
 * removes show up without local state. The parent Drawer owns the
 * mount/unmount lifecycle; this component is a pure render of the
 * frame list.
 */
export function FramesPanel({ doc, onClose }: FramesPanelProps) {
  const binding = useCanvasBinding();
  const frames = useFrames(doc, binding);
  const canvas = useImperative();

  // Click → expand if collapsed, fit-to-view the frame's bounds,
  // and select it. Three discrete operations:
  //  1. Yjs write: data.collapsed=false (only when currently true).
  //     Direct doc mutation — collapse state is canvas content,
  //     not RF state, so the imperative bridge doesn't expose it.
  //  2. RF fitView with nodes=[frame.id] — RF computes the right
  //     viewport to fit JUST that frame with default padding,
  //     which honors the frame's expanded size after the Yjs
  //     write commits.
  //  3. RF setNodes selection update so the frame's floating
  //     toolbar appears.
  const handleClick = useCallback(
    (frame: FrameSummary) => {
      if (!canvas) return;

      // Expand if collapsed. Direct Yjs write — collapse state is
      // canvas content, not RF state. Track whether we needed to
      // expand so the fitView call below can defer until RF has
      // re-measured the now-expanded frame.
      let didExpand = false;
      if (doc) {
        const node = canvasRoots(doc, binding).nodes.get(frame.id);
        const data = node?.get('data');
        if (data instanceof Y.Map && data.get('collapsed') === true) {
          doc.transact(() => data.set('collapsed', false), doc.clientID);
          didExpand = true;
        }
      }

      // Sole selection so the floating toolbar appears immediately.
      // Safe to fire synchronously — RF's setNodes uses its current
      // node array regardless of frame size.
      canvas.setNodes((nodes) =>
        nodes.map((n) => ({ ...n, selected: n.id === frame.id })),
      );

      // Fit-to-view with nodes=[frame.id]. When we just flipped
      // collapsed→false, RF still has the COLLAPSED bounds in its
      // internal store at this instant. Calling fitView immediately
      // would zoom to the small folder bounds, then the frame
      // re-renders larger and the user ends up looking at a
      // shrunken view. Defer one rAF so Yjs → useNodesFromYjs →
      // MergeMap → RF measurement completes first; fitView then
      // reads the fresh expanded bounds. When the frame was
      // already expanded, no defer is needed — fire immediately.
      const fit = () =>
        canvas.fitView({ nodes: [{ id: frame.id }], duration: 400, padding: 0.1 });
      if (didExpand) {
        // Two rAFs: one for the Yjs commit + React render, one
        // for RF's post-render measurement pass. Belt-and-suspenders;
        // observed in practice that one isn't always enough on slow
        // devices.
        requestAnimationFrame(() => requestAnimationFrame(fit));
      } else {
        fit();
      }
    },
    [canvas, doc, binding],
  );

  return (
    <Flex vertical className={styles.root}>
      {/* Header — mirrors AssistantPanel's spacing + chrome:
          space-between flex, title on the left, X close on the right,
          var(--space-4) var(--space-5) padding, bottom border. */}
      <Flex align="center" justify="space-between" className={styles.header}>
        <Flex align="center" gap="sm" className={styles.headerLeft}>
          <Text size="sm" strong>
            Frames
          </Text>
          <Text size="xs" type="secondary">
            {frames.length}
          </Text>
        </Flex>
        {onClose && (
          <Tooltip title="Close" placement="bottom">
            <IconButton
              size="sm"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close frames panel"
            >
              <Icon name="close" size={16} weight={iconWeight.light} />
            </IconButton>
          </Tooltip>
        )}
      </Flex>

      {frames.length === 0 ? (
        <Flex vertical align="center" justify="center" className={styles.empty}>
          <Empty description="No frames yet. Create one with the Frame tool." />
        </Flex>
      ) : (
        <Flex vertical className={styles.list} role="list">
          {frames.map((frame) => (
            <button
              key={frame.id}
              type="button"
              role="listitem"
              className={clsx(styles.row)}
              onClick={() => handleClick(frame)}
            >
              <Icon name={frame.iconName} size={icon.md} className={styles.rowIcon} />
              <span className={styles.rowTitle}>
                {frame.title.trim() || 'Untitled'}
              </span>
              <span className={styles.rowCount}>
                {frame.childCount} {frame.childCount === 1 ? 'item' : 'items'}
              </span>
            </button>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
